import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, concatMap, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { COMPANY_FILTER_CHANGE } from 'containers/PartnerOptions/constants'

import {
  fetchTrackedEmailDetailError,
  fetchTrackedEmailDetailSuccess,
  fetchTrackedEmails,
  fetchTrackedEmailsError,
  fetchTrackedEmailsSuccess,
  fetchTrackedEmailsSummaryError,
  fetchTrackedEmailsSummarySuccess,
  fetchTriggerPoliciesConfig,
  fetchTriggerPoliciesConfigError,
  fetchTriggerPoliciesConfigSuccess,
  fetchTriggerPoliciesSchema,
  fetchTriggerPoliciesSchemaError,
  fetchTriggerPoliciesSchemaSuccess,
} from './actions'
import { buildTrackedEmailsQuery } from './buildTrackedEmailsQuery'
import {
  CHANGE_MESSAGE_ACCESS_ASYNC,
  DEFAULT_TRACKED_EMAILS_PAGE_SIZE,
  FETCH_TRACKED_EMAIL_DETAIL_ASYNC,
  FETCH_TRACKED_EMAILS_ASYNC,
  FETCH_TRACKED_EMAILS_SUMMARY_ASYNC,
  FETCH_TRIGGER_POLICIES_CONFIG_ASYNC,
  FETCH_TRIGGER_POLICIES_SCHEMA_ASYNC,
  GO_TO_TRACKED_EMAILS_NEXT_PAGE,
  GO_TO_TRACKED_EMAILS_PREV_PAGE,
  SET_TRACKED_EMAILS_FILTERS,
} from './constants'
import {
  isOtherCompany,
  mapTrackedEmailRow,
  mapTrackedEmailsResponse,
  mapTrackedEmailsSummaryResponse,
} from './mapTrackedEmails'
import { selectTrackingCompanyId } from './selectors'

const getCompanyId = (state$) => selectTrackingCompanyId(state$.value)

const getTrackingState = (state$) => state$.value.emailencryption?.emailTracking || {}

const buildListFiltersFromStore = (state$, overrideFilters) => {
  const tracking = getTrackingState(state$)
  const policiesAvailable = Boolean(
    tracking.schemaLoaded && tracking.configLoaded && !tracking.schemaError && !tracking.configError
  )

  return {
    ...tracking.filters,
    ...overrideFilters,
    size: DEFAULT_TRACKED_EMAILS_PAGE_SIZE,
    cursor: tracking.cursor || undefined,
    policiesAvailable,
  }
}

const trackedEmailsFiltersEpic = (action$, state$) =>
  action$.pipe(
    ofType(SET_TRACKED_EMAILS_FILTERS),
    mergeMap(() => {
      const tracking = getTrackingState(state$)
      const actions = [fetchTrackedEmails()]

      if (!tracking.schemaLoaded && !tracking.schemaLoader) {
        actions.push(fetchTriggerPoliciesSchema())
      }
      if (!tracking.configLoaded && !tracking.configLoader) {
        actions.push(fetchTriggerPoliciesConfig())
      }

      return of(...actions)
    })
  )

const trackedEmailsPaginationEpic = (action$) =>
  action$.pipe(
    ofType(GO_TO_TRACKED_EMAILS_NEXT_PAGE, GO_TO_TRACKED_EMAILS_PREV_PAGE),
    map(() => fetchTrackedEmails())
  )

const fetchTrackedEmailsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_TRACKED_EMAILS_ASYNC.INITIATE),
    switchMap((action) => {
      const companyId = getCompanyId(state$)
      const filters = buildListFiltersFromStore(state$, action.payload?.filters)
      const params = buildTrackedEmailsQuery(filters)

      return api.emailEncryption.getTrackedEmails(params, companyId).pipe(
        map((response) => fetchTrackedEmailsSuccess(mapTrackedEmailsResponse(response))),
        catchError((error) => of(epicsErrorHandler(fetchTrackedEmailsError, error)))
      )
    })
  )

const fetchTrackedEmailsSummaryEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_TRACKED_EMAILS_SUMMARY_ASYNC.INITIATE),
    switchMap((action) => {
      const companyId = action.payload?.companyId ?? getCompanyId(state$)

      return api.emailEncryption.getTrackedEmailsSummary(action.payload?.timeFrame, companyId).pipe(
        map((response) =>
          fetchTrackedEmailsSummarySuccess(mapTrackedEmailsSummaryResponse(response))
        ),
        catchError((error) => of(epicsErrorHandler(fetchTrackedEmailsSummaryError, error))),
        takeUntil(
          action$.pipe(
            ofType(COMPANY_FILTER_CHANGE),
            filter((change) => isOtherCompany(change, companyId))
          )
        )
      )
    })
  )

const fetchTrackedEmailDetailEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_TRACKED_EMAIL_DETAIL_ASYNC.INITIATE),
    switchMap((action) => {
      const companyId = getCompanyId(state$)
      const messageIdNormalized = action.payload?.messageIdNormalized

      if (!messageIdNormalized) {
        return of(fetchTrackedEmailDetailError({ message: 'Missing message id' }))
      }

      return api.emailEncryption.getTrackedEmailDetail(messageIdNormalized, companyId).pipe(
        map((response) => {
          const raw = response?.data ?? response
          return fetchTrackedEmailDetailSuccess(mapTrackedEmailRow(raw))
        }),
        catchError((error) => of(epicsErrorHandler(fetchTrackedEmailDetailError, error)))
      )
    })
  )

const MESSAGE_ACCESS_EXPECTED_ERRORS = [400, 404, 409]

const changeMessageAccessEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CHANGE_MESSAGE_ACCESS_ASYNC.INITIATE),
    concatMap((action) => {
      const msgGuid = action.payload?.msgGuid
      const messageIdNormalized = action.payload?.messageIdNormalized
      const access = action.payload?.access
      if (!msgGuid || !messageIdNormalized || (access !== 'revoke' && access !== 'restore')) {
        return of(CHANGE_MESSAGE_ACCESS_ASYNC.onError({ detail: 'Missing message id' }))
      }

      const companyId = getCompanyId(state$)
      return api.emailEncryption.changeMessageAccess(access, msgGuid, companyId).pipe(
        mergeMap((response) => {
          const body = response?.response ?? response
          return of(
            CHANGE_MESSAGE_ACCESS_ASYNC.onSuccess({
              msgGuid: messageIdNormalized,
              access,
              at: Date.now(),
              grantsAffected: body?.grantsAffected ?? null,
              alreadyInState: Boolean(body?.alreadyInState),
            })
          )
        }),
        catchError((error) => {
          let failed
          try {
            failed = epicsErrorHandler(
              CHANGE_MESSAGE_ACCESS_ASYNC.onError,
              error,
              MESSAGE_ACCESS_EXPECTED_ERRORS
            )
          } catch {
            failed = CHANGE_MESSAGE_ACCESS_ASYNC.onError({
              detail: error?.response?.detail || 'Could not update email access.',
              status: error?.status,
            })
          }
          const detail = error?.response?.detail
          if (typeof detail === 'string' && detail && failed.error) failed.error.detail = detail
          return of(failed)
        })
      )
    })
  )

const fetchTriggerPoliciesSchemaEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_TRIGGER_POLICIES_SCHEMA_ASYNC.INITIATE),
    switchMap(() => {
      const companyId = getCompanyId(state$)

      return api.settings.getEmailEncryptionPolicyCategories(companyId).pipe(
        map((response) => fetchTriggerPoliciesSchemaSuccess(response?.data ?? response)),
        catchError((error) => of(epicsErrorHandler(fetchTriggerPoliciesSchemaError, error)))
      )
    })
  )

const fetchTriggerPoliciesConfigEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_TRIGGER_POLICIES_CONFIG_ASYNC.INITIATE),
    switchMap(() => {
      const companyId = getCompanyId(state$)

      return api.settings.getEmailEncryptionConfig(companyId).pipe(
        map((response) => {
          const raw = response?.data ?? response
          const config = raw?.response || raw?.data || raw
          return fetchTriggerPoliciesConfigSuccess(config)
        }),
        catchError((error) => of(epicsErrorHandler(fetchTriggerPoliciesConfigError, error)))
      )
    })
  )

export default combineEpics(
  trackedEmailsFiltersEpic,
  trackedEmailsPaginationEpic,
  fetchTrackedEmailsEpic,
  fetchTrackedEmailsSummaryEpic,
  fetchTrackedEmailDetailEpic,
  changeMessageAccessEpic,
  fetchTriggerPoliciesSchemaEpic,
  fetchTriggerPoliciesConfigEpic
)
